import BigNumber from 'bignumber.js';

// Token Distribution Constants
export const CONSTANTS = {
    // Token Supply Distribution
    TOTAL_SUPPLY: 100_000_000,      // Total JINU supply
    AUCTION_SUPPLY: 37_500_000,     // 37.5% for auction
    LIQUIDITY_SUPPLY: 37_500_000,   // 37.5% for liquidity
    REWARDS_SUPPLY: 10_000_000,     // 10% for rewards pool
    AFFILIATES_SUPPLY: 10_000_000,  // 10% for affiliates
    RESERVE_SUPPLY: 5_000_000,      // 5% reserve

    // Auction Parameters
    TOTAL_DAYS: 51,                 // 50 auction days + 1 DEX listing day
    AUCTION_DAYS: 50,               // Number of actual auction days
    SOL_PRICE_USD: 200,            // 1 SOL = $200
    START_PRICE: 0.001,            // Fixed start price in USD
    DEX_MULTIPLIER: 2,             // For projected DEX price calculation
    DECIMALS: 9                    // Token decimals
};

// Create a factory function that returns a new instance of StatsCalculator
export function createStatsCalculator() {
    // Store real contributions per day
    const realContributions = new Map(); // day => { contributions: [] }

    // Cache for simulated contributions
    const simulatedContributionsCache = new Map();

    // Cache for daily totals
    const dailyTotalsCache = new Map();

    // Seeded random number generator for consistency
    const seededRandom = (seed) => {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
    };

    return {
        // Initialize the calculator
        init: function() {
            // Clear any existing caches
            this.clearCaches();
            return this;
        },
        
        // Add real contribution to a day
        addRealContribution: function(day, amount, address, currentDay, isAllDays = false) {
            if (day < currentDay) {
                throw new Error('Cannot contribute to past days');
            }
            if (day > CONSTANTS.AUCTION_DAYS) {
                throw new Error('Cannot contribute to DEX listing day');
            }

            if (isAllDays) {
                // Calculate remaining days (current to day 50)
                // No +1 needed because if we're on day 19, and auction ends on day 50
                // then remaining days = 50 - 19 = 31 days
                const remainingDays = CONSTANTS.AUCTION_DAYS - currentDay;
                
                // Calculate amount per day (rounded to 8 decimal places for precision)
                const amountPerDay = new BigNumber(amount)
                    .dividedBy(remainingDays)
                    .decimalPlaces(8)
                    .toNumber();

                // Add contribution to each remaining day starting from current day
                for (let d = currentDay; d <= CONSTANTS.AUCTION_DAYS; d++) {
                    const dayContributions = realContributions.get(d) || { contributions: [] };
                    dayContributions.contributions.push({
                        amount: amountPerDay,
                        address,
                        timestamp: Date.now(),
                        isAllDays: true
                    });
                    realContributions.set(d, dayContributions);
                    dailyTotalsCache.delete(d);
                }
            } else {
                // Single day contribution
                const dayContributions = realContributions.get(day) || { contributions: [] };
                dayContributions.contributions.push({
                    amount: new BigNumber(amount).toNumber(),
                    address,
                    timestamp: Date.now(),
                    isAllDays: false
                });
                realContributions.set(day, dayContributions);
                dailyTotalsCache.delete(day);
            }
        },

        // Get total real contributions for a day
        getRealContributions: function(day) {
            const dayData = realContributions.get(day) || { contributions: [] };
            return dayData.contributions.reduce(
                (total, contribution) => new BigNumber(total).plus(contribution.amount).toNumber(),
                0
            );
        },

        // Add a simulated contribution for a day
        addSimulatedContribution: function(day, amount) {
            if (!simulatedContributionsCache.has(day)) {
                simulatedContributionsCache.set(day, []);
            }
            simulatedContributionsCache.get(day).push({
                amount: amount,
                timestamp: Date.now()
            });
            // Clear daily totals cache for this day
            dailyTotalsCache.delete(day);
        },
        
        // Get simulated contribution for a day (50-150 SOL)
        getSimulatedContribution: function(day) {
            if (day > CONSTANTS.AUCTION_DAYS) return 0;
            
            // Check cache first
            if (simulatedContributionsCache.has(day)) {
                return simulatedContributionsCache.get(day).reduce((total, contribution) => new BigNumber(total).plus(contribution.amount).toNumber(), 0);
            }
            
            // Generate a consistent random value for this day using the day number as seed
            // This ensures the same value is generated for each day, even after page refresh
            const seed = day * 1000;
            const amount = new BigNumber(50)
                .plus(new BigNumber(seededRandom(seed)).times(100))
                .integerValue(BigNumber.ROUND_FLOOR)
                .toNumber();
            
            // Cache the result
            simulatedContributionsCache.set(day, [{ amount, timestamp: Date.now() }]);
            return amount;
        },

        // Get total for a specific day (simulated + real)
        getDailyTotal: function(day) {
            if (day > CONSTANTS.AUCTION_DAYS) {
                return 0;
            }
            
            // Check cache first
            if (dailyTotalsCache.has(day)) {
                return dailyTotalsCache.get(day);
            }
            
            const simulated = this.getSimulatedContribution(day);
            const real = this.getRealContributions(day);
            const total = new BigNumber(simulated).plus(real).toNumber();

            // Cache the result
            dailyTotalsCache.set(day, total);
            return total;
        },

        // Get user's allocation percentage for a specific day
        getUserAllocationPercentage: function(day, address) {
            const dayTotal = this.getDailyTotal(day);
            if (dayTotal === 0) return 0;

            const dayData = realContributions.get(day) || { contributions: [] };
            const userContributions = dayData.contributions
                .filter(c => c.address === address)
                .reduce(
                    (total, contribution) => new BigNumber(total).plus(contribution.amount).toNumber(),
                    0
                );

            return new BigNumber(userContributions)
                .dividedBy(dayTotal)
                .times(100)
                .toNumber();
        },

        // Get daily JINU allocation for a user
        getUserDailyJinuAllocation: function(day, address) {
            const percentage = this.getUserAllocationPercentage(day, address);
            const dailyJinu = new BigNumber(CONSTANTS.AUCTION_SUPPLY)
                .dividedBy(CONSTANTS.AUCTION_DAYS)
                .toNumber();

            return new BigNumber(dailyJinu)
                .times(percentage)
                .dividedBy(100)
                .toNumber();
        },

        // Calculate total SOL raised up to a specific day
        calculateTotalSOLRaised: function(currentDay) {
            let total = new BigNumber(0);
            for (let day = 1; day <= currentDay; day++) {
                total = total.plus(this.getDailyTotal(day));
            }
            return total.toNumber();
        },
        
        // Clear all caches
        clearCaches: function() {
            realContributions.clear();
            simulatedContributionsCache.clear();
            dailyTotalsCache.clear();
        }
    };
}

// Create a default instance for backward compatibility
const StatsCalculator = createStatsCalculator();

export default StatsCalculator;
